import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Box,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  Pagination,
  Button,
  Modal,
  Input,
  Select,
  InputNumber,
  notification,
  Empty,
} from "antd";
import { PlusOutlined, MinusOutlined, GiftOutlined } from "@ant-design/icons";
import React, { useMemo, useState, useEffect } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { Controller } from "Controller/Controller";

const { Option } = Select;

function MemberRewardTable(props) {
  const { memberId } = props;

  // Data State
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustType, setAdjustType] = useState("add"); // or 'deduct'
  const [adjustPoints, setAdjustPoints] = useState(0);
  const [adjustDescription, setAdjustDescription] = useState("");

  // Styles
  const textColor = useColorModeValue("navy.700", "white");
  const accentColor = "#009900";

  // Columns
  const columnsData = useMemo(
    () => [
      {
        Header: "#",
        accessor: "serial_number",
        disableSortBy: true,
        Cell: ({ row }) =>
          (pagination.current - 1) * pagination.pageSize + row.index + 1,
      },
      {
        Header: "Amount",
        accessor: "amount",
      },
      {
        Header: "transaction_type",
        accessor: "transaction_type",
      },
      {
        Header: "created_at",
        accessor: "created_at",
      },
      {
        Header: "description",
        accessor: "description",
      },
    ],
    [pagination.current, pagination.pageSize]
  );

  const tableInstance = useTable(
    {
      columns: columnsData,
      data,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  // --- fetching ---
  const fetchRewardPoints = async () => {
    if (!memberId) return;
    try {
      const officeId = localStorage.getItem("selectedOffice");
      const res = await Controller.GetRewardPoints(officeId, memberId);

      if (res.status < 300) {
        let balance = 0;

        if (res.json && res.json.total_balance !== undefined) {
          balance = res.json.total_balance;
        } else if (res.json.results) {
          if (res.json.total_balance !== undefined)
            balance = res.json.total_balance;
          else if (
            res.json.results.length > 0 &&
            res.json.results[0].balance !== undefined
          ) {
            balance = res.json.results[0].balance;
          }
        } else if (Array.isArray(res.json)) {
          if (res.json.length > 0 && res.json[0].balance !== undefined) {
            balance = res.json[0].balance;
          }
        }

        setTotalPoints(balance);
      } else {
        setTotalPoints(0);
      }
    } catch (err) {
      console.error("Error fetching rewards", err);
    }
  };

  const fetchRewardTransactions = async (page = 1) => {
    if (!memberId) return;
    setLoading(true);
    try {
      const officeId = localStorage.getItem("selectedOffice");
      const res = await Controller.GetRewardTransactions(page, officeId, memberId);

      if (res.status < 300) {
        const list = res.json.results || [];
        const count = res.json.count || 0;
        setData(list);
        setPagination((prev) => ({ ...prev, current: page, total: count }));
      } else {
        setData([]);
        setPagination((prev) => ({ ...prev, current: page, total: 0 }));
      }
    } catch (err) {
      console.error("Error fetching reward transactions", err);
      setData([]);
      setPagination((prev) => ({ ...prev, current: page, total: 0 }));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRewardPoints();
    fetchRewardTransactions(1);
  }, [memberId]);

  // --- Adjustment ---

  const handleAdjustSubmit = async (type) => {
    if (!adjustPoints || adjustPoints <= 0) {
      notification.error({ message: "Please enter a valid point amount" });
      return;
    }

    setAdjustLoading(true);
    try {
      const officeId = localStorage.getItem("selectedOffice");
      const finalPoints =
        type === "deduct" ? -Math.abs(adjustPoints) : Math.abs(adjustPoints);

      const payload = {
        office_id: officeId,
        customer_id: memberId,
        points: finalPoints,
        description:
          adjustDescription ||
          (type === "add"
            ? "Manual Adjustment (Credit)"
            : "Manual Adjustment (Debit)"),
        transaction_type: "adjustment",
      };

      const res = await Controller.ManualPointAdjustment(payload);

      if (res.status < 300) {
        notification.success({ message: "Points updated successfully" });
        fetchRewardPoints();
        fetchRewardTransactions(pagination.current);
        setAdjustPoints(0);
        setAdjustDescription("");
      } else {
        notification.error({
          message: res.message || "Failed to adjust points",
        });
      }
    } catch (e) {
      console.error(e);
      notification.error({ message: "An error occurred" });
    }
    setAdjustLoading(false);
  };

  return (
    <Box w="100%" overflowX={{ sm: "scroll", lg: "hidden" }}>
      {/* Header & Balance */}
      <Flex
        align="center"
        justify="space-between"
        px="22px"
        mb="20px"
        boxShadow="0px 1px 0px 0px rgba(0, 0, 0, 0.05)"
        pb="15px"
      >
        <Text color={textColor} fontSize="xl" fontWeight="700">
          Rewards
        </Text>

        <Flex align="center">
          <Text color="gray.500" fontSize="sm" mr="8px" fontWeight="600">
            Current Balance:
          </Text>
          <Text color={accentColor} fontSize="2xl" fontWeight="800">
            {totalPoints}
          </Text>
          <Text color="gray.400" fontSize="xs" ml="4px" mt="6px">
            PTS
          </Text>
        </Flex>
      </Flex>

      {/* Inline Adjustment Form */}
      <Box px="22px" mb="25px">
        <Text
          fontSize="xs"
          fontWeight="bold"
          color="gray.500"
          mb="8px"
          textTransform="uppercase"
        >
          Quick Adjustment
        </Text>
        <Flex gap="10px" align="end" wrap="wrap">
          <Box>
            <Input
              placeholder="Points"
              value={adjustPoints}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/[^0-9]/g, "");
                setAdjustPoints(numericValue ? parseInt(numericValue, 10) : 0);
              }}
              style={{ width: "120px", height: "39px" }}
              size="middle"
            />
          </Box>
          <Box flex="1" minW="200px">
            <Input
              placeholder="Description (Optional)"
              value={adjustDescription}
              onChange={(e) => setAdjustDescription(e.target.value)}
              size="middle"
              style={{ height: "39px" }}
            />
          </Box>
          <Flex gap="8px">
            <Button
              type="primary"
              onClick={() => handleAdjustSubmit("add")}
              loading={adjustLoading}
              icon={<PlusOutlined />}
              style={{ height: "39px" }}
            >
              Increase
            </Button>
            <Button
              danger
              type="text"
              onClick={() => handleAdjustSubmit("deduct")}
              loading={adjustLoading}
              icon={<MinusOutlined />}
              style={{ height: "39px" }}
            >
              Decrease
            </Button>
          </Flex>
        </Flex>
      </Box>

      {/* History Table */}
      <Box px="22px">
        {/* <Text
          fontSize="xs"
          fontWeight="bold"
          color="gray.500"
          mb="10px"
          textTransform="uppercase"
        >
          Transaction History
        </Text> */}
        {data.length === 0 ? (
          <Empty
            description="No history available"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Table
            {...getTableProps()}
            variant="simple"
            color="gray.500"
            size="sm"
          >
            <Thead>
              {headerGroups.map((headerGroup, index) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, i) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      pe="10px"
                      key={i}
                      borderColor="transparent"
                      py="12px"
                    >
                      <Flex
                        justify="space-between"
                        align="center"
                        fontSize={{ sm: "10px", lg: "11px" }}
                        color="gray.400"
                      >
                        {column.render("Header")}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {rows.map((row, index) => {
                prepareRow(row);
                return (
                  <Tr {...row.getRowProps()} key={index}>
                    {row.cells.map((cell, i) => (
                      <Td
                        {...cell.getCellProps()}
                        key={i}
                        fontSize={{ sm: "13px" }}
                        borderColor="gray.100"
                        py="10px"
                      >
                        {cell.column.id === "created_at" && cell.value
                          ? new Date(cell.value).toLocaleString()
                          : cell.render("Cell")}
                      </Td>
                    ))}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}

        {/* Pagination */}
        {pagination.total > pagination.pageSize && (
          <Flex justify="center" align="center" py="15px" w="100%">
            <Pagination
              current={pagination.current}
              total={pagination.total}
              pageSize={pagination.pageSize}
              onChange={(p) => fetchRewardTransactions(p)}
              showSizeChanger={false}
              size="small"
            />
          </Flex>
        )}
      </Box>
    </Box>
  );
}

export default MemberRewardTable;
